import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Link2, Link2Off, Save } from 'lucide-react';
import { Badge, Button, Card } from '../components/ui';
import { MockAPI } from '../services/api';
import { getAuthToken } from '../services/auth';
import { Document, Project, RecordLink } from '../types';
import {useAuth} from '../services/authContext';
import {useFarm} from '../services/farmContext';
import PhotoCaptureFlow from '../components/PhotoCaptureFlow';
import EntityPhotoCollection from '../components/photos/EntityPhotoCollection';
import {journalFarm} from '../services/photoJournal';
import {usePhotoNavigationGuard} from '../services/photos/usePhotoNavigationGuard';

const statusLabel: Record<Project['status'], string> = {
  todo: 'To do',
  in_process: 'In process',
  paused: 'Paused',
  completed: 'Completed',
  archived: 'Archived',
};

const DocumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate(),[params]=useSearchParams();
  const {user,privileges}=useAuth(),{activeFarmId}=useFarm();
  const canEdit=!!privileges.documents?.write,canLink=canEdit&&Object.entries(privileges).some(([key,value])=>key==='maintenance'&&value.write);
  const [capture,setCapture]=useState(false),[fallbackFarm,setFallbackFarm]=useState('');
  const [documentItem, setDocumentItem] = useState<Document | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [links, setLinks] = useState<RecordLink[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoDraft,setPhotoDraft]=useState(false);
  usePhotoNavigationGuard(photoDraft||!!documentItem&&(editTitle!==(documentItem.title||'')||editNotes!==(documentItem.notes||'')));
  const editor=useRef({id:documentItem?.document_id,title:editTitle,notes:editNotes,originalTitle:documentItem?.title||'',originalNotes:documentItem?.notes||''});
  editor.current={id:documentItem?.document_id,title:editTitle,notes:editNotes,originalTitle:documentItem?.title||'',originalNotes:documentItem?.notes||''};

  const refresh = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [doc, linkableProjects, docLinks] = await Promise.all([
        MockAPI.getDocument(id),
        privileges.projects?.view?MockAPI.getLinkableProjects():Promise.resolve([]),
        MockAPI.getRecordLinksForDocument(id),
      ]);
      const draft=editor.current,keepDraft=draft.id===doc?.document_id&&(draft.title!==draft.originalTitle||draft.notes!==draft.originalNotes);
      setDocumentItem(doc || null);
      if(!keepDraft){setEditTitle(doc?.title || '');setEditNotes(doc?.notes || '');}
      setProjects(linkableProjects);
      setLinks(docLinks.filter((link) => link.to_record_type === 'project' || link.from_record_type === 'project'));
      setError(null);
    } catch (err) {
      console.error('Failed to load document detail', err);
      setError('Unable to load document detail.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [id]);

  useEffect(()=>{let alive=true;if(!activeFarmId&&!documentItem?.farm_id)void journalFarm().then(farm=>{if(alive)setFallbackFarm(farm.farm_id);}).catch(()=>{});return()=>{alive=false;};},[activeFarmId,documentItem?.farm_id]);
  useEffect(()=>{const photo=params.get('photo');if(photo&&documentItem)navigate('/documents/'+encodeURIComponent(documentItem.document_id)+'/photos/'+encodeURIComponent(photo),{replace:true});},[params.get('photo'),documentItem?.document_id]);
  const photoFarm=documentItem?.farm_id||activeFarmId||fallbackFarm;
  const linkedProjectIds = useMemo(() => new Set(links.map((link) => link.to_record_type === 'project' ? link.to_record_id : link.from_record_id)), [links]);
  const linkedProjects = useMemo(() => {
    const byId = new Map(projects.map((project) => [project.id, project]));
    return links.map((link) => { const projectId = link.to_record_type === 'project' ? link.to_record_id : link.from_record_id; return { link, project: byId.get(projectId) }; }).filter((entry) => !!entry.project);
  }, [links, projects]);

  const saveDocumentMeta = async () => {
    if (!documentItem || !canEdit) return;
    setWorking(true);
    try {
      const updatedDoc = await MockAPI.saveDocument({
        ...documentItem,
        title: editTitle.trim() || documentItem.title,
        notes: editNotes,
      });
      setDocumentItem(updatedDoc);
      setEditTitle(updatedDoc.title || '');
      setEditNotes(updatedDoc.notes || '');
      setError(null);
    } catch (err) {
      console.error('Failed to save document metadata', err);
      setError('Unable to save document metadata.');
    } finally {
      setWorking(false);
    }
  };

  const handleDownload = async () => {
    if (!documentItem) return;

    const directSource = documentItem.file_base64;
    if (directSource) {
      const link = document.createElement('a');
      link.href = directSource;
      link.download = documentItem.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    const downloadUrl = documentItem.presigned_url || documentItem.file_path;
    if (!downloadUrl) {
      setError('This document cannot be downloaded.');
      return;
    }

    setWorking(true);
    try {
      const headers: HeadersInit = {};
      const token = getAuthToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(downloadUrl, { headers });
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = documentItem.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      setError(null);
    } catch (err) {
      console.error('Failed to download document', err);
      setError('Unable to download this document.');
    } finally {
      setWorking(false);
    }
  };

  const linkProject = async () => {
    if (!id || !selectedProjectId) return;
    setWorking(true);
    try {
      await MockAPI.linkDocumentToProject(id, selectedProjectId);
      setSelectedProjectId('');
      await refresh();
    } catch (err) {
      console.error('Failed to link project', err);
      setError('Unable to link project to this document.');
    } finally {
      setWorking(false);
    }
  };

  const unlinkProject = async (recordLinkId: string) => {
    setWorking(true);
    try {
      await MockAPI.unlinkDocumentFromProject(recordLinkId);
      await refresh();
    } catch (err) {
      console.error('Failed to unlink project', err);
      setError('Unable to unlink project from this document.');
    } finally {
      setWorking(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-500">Loading document...</div>;
  if (!documentItem) return <div className="p-8 text-gray-500">Document not found.</div>;

  return (
    <div className="space-y-6">
      {capture&&photoFarm&&canEdit&&<PhotoCaptureFlow initialDocumentId={documentItem.document_id} initialDestinationLabel={documentItem.title} initialFarmId={photoFarm} onCancel={()=>setCapture(false)} onComplete={()=>setCapture(false)}/>}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate('/documents')}><ArrowLeft className="mr-2 h-4 w-4" />Back to Document Vault</Button>
      </div>

      {error && <div className="rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <Card className="p-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
          <input
            aria-label="Document title"
            type="text"
            className="w-full rounded-md border border-gray-300 p-2"
            disabled={!canEdit||working}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            aria-label="Document notes"
            className="w-full rounded-md border border-gray-300 p-2"
            rows={3}
            disabled={!canEdit||working}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={saveDocumentMeta} disabled={working||!canEdit}><Save className="mr-2 h-4 w-4" />Save</Button>
          <Button variant="outline" onClick={handleDownload} disabled={working}><Download className="mr-2 h-4 w-4" />Download</Button>
        </div>
      </Card>

      {photoFarm?<EntityPhotoCollection context={{kind:'document',farmId:photoFarm,entityId:documentItem.document_id}} onDirtyChange={setPhotoDraft} actions={canEdit?<Button onClick={()=>setCapture(true)}>Add photo</Button>:undefined}/>:<p className="text-sm text-gray-500">Choose a property to view document photos.</p>}

      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Link to Project</h2>
        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            className="w-full rounded-md border border-gray-300 p-2 sm:max-w-md"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            <option value="">Select active project...</option>
            {projects.filter((project) => !linkedProjectIds.has(project.id)).map((project) => (
              <option key={project.id} value={project.id}>{project.name} ({statusLabel[project.status]})</option>
            ))}
          </select>
          <Button onClick={linkProject} disabled={!selectedProjectId || working||!canLink}><Link2 className="mr-2 h-4 w-4" />Link</Button>
        </div>

        <div className="space-y-2">
          {linkedProjects.length === 0 ? (
            <p className="text-sm text-gray-500">No linked projects.</p>
          ) : linkedProjects.map(({ link, project }) => (
            <div key={link.record_link_id} className="flex items-center justify-between rounded border border-gray-200 p-2">
              <div className="flex items-center gap-2">
                <Link className="text-sm text-usda-700 hover:underline" to={`/projects/${project!.id}`}>{project!.name}</Link>
                <Badge>{statusLabel[project!.status]}</Badge>
              </div>
              <Button size="sm" variant="outline" onClick={() => unlinkProject(link.record_link_id)} disabled={working||!canLink}><Link2Off className="mr-1 h-3.5 w-3.5" />Unlink</Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default function ScopedDocumentDetail(){const {user,privileges}=useAuth(),{id}=useParams();return <DocumentDetail key={JSON.stringify([id,user?.email,user?.status,user?.farm_ids,user?.roles,privileges])}/>;}
